/*
 |------------------------------------------------------------------------------
 | A central place to fetch and query content provided by Nuxt Content module.
 |------------------------------------------------------------------------------
 |
 */

// Nuxt / Vendor
import { Context } from "@nuxt/types";
import { FetchReturn, QueryBuilder } from "@nuxt/content/types/query-builder";

// Project
import {
    NodeType, ContentNode, PageContentNode, PartialContentNode, SiblingLink, NodeSiblingLinks, TestimonialContentNode
} from "./Types";

export const ContentPathsMap = new Map([
    ["pages", NodeType.page],
    ["blog", NodeType.blog],
    ["partials", NodeType.partial],
    ["testimonials", NodeType.testimonial]
]);

export interface FeaturedTestimonialsItem {
    index: number;
    name: string;
}

export interface QuerySort {
    field: string;
    direction: "asc" | "desc" | undefined;
}

/**
 * Single service class for resolving and looking up content.
 */
export default class ContentGateway {
    protected appContext: Context;

    /**
     * Default node type content folder if not found in the map. All node types
     * are namespaced to their on dedicated sub path folder, with the exception
     * of pages that are able to be the website root as stand-alone pages as
     * well as used sub-folders.
     *
     * @type {String}
     */
    protected defaultNodeType = "pages";

    /**
     * Map of paths to type for content items that are not pages.
     * @type {Array<string>}
     */
    protected nodeMap: Array<string> = ["pages", "blog"];

    /**
     * Content Gateway Constructor.
     *
     * @param {Context} context
     */
    public constructor (context: Context) {
        this.appContext = context;
    }

    /**
     * A simple factory helper.
     *
     * @param {Context} context
     *
     * @returns ContentGateway
     */
    public static make (context: Context): ContentGateway {
        return new ContentGateway(context);
    }

    /**
     * Function that returns true if the current environment is development.
     *
     * @returns boolean
     */
    public isDev (): boolean {
        return this.appContext.$config.isDev;
    }

    /**
     * Convert a string path / type to a Node Type.
     *
     * @param  {ContentNode}   node
     *
     * @return {NodeType}
     */
    public getType (node: ContentNode): NodeType {
        const nodePath = node.dir.substring(1);
        return ContentPathsMap.get(nodePath) ?? NodeType.undefined;
    }

    /**
     * Find a specific page by path.
     *
     * @param {string} path
     *
     * @returns ContentNodeInterface
     * @throws Error
     */
    public async getNode (path: string): Promise<ContentNode> {
        let nodeType = null;
        let nodePath = path;

        for (const dir of this.nodeMap) {
            if (path.startsWith(dir)) {
                nodeType = dir;
                nodePath = path.substring(dir.length);
                break;
            }
        }

        // Handle pages in the website root
        if (nodeType === null) {
            nodeType = this.defaultNodeType;
        }

        // Handle directory rout
        if (nodePath.length === 0) {
            nodePath = "index";
        }

        let node = null;
        const nodes = await this.content(nodeType, nodePath).fetch();

        // Exact match
        if (nodes instanceof Object) {
            node = nodes;
        }

        // Directory match, lookup index
        if (nodes instanceof Array) {
            node = nodes.find(node => node.slug === "index");
        }

        // Page not found
        if (!node) {
            throw new Error("Content node could not be found");
        }

        // Set type
        const contentNode = (node as unknown) as ContentNode;
        contentNode.nodeType = this.getType(contentNode);

        // Type specific properties
        if (contentNode.nodeType === NodeType.blog) {
            const sortBy = { field: "sequence", direction: "desc" } as QuerySort;
            contentNode.siblingLinks = await this.getNodeSiblings(contentNode, sortBy);
        }

        return contentNode;
    }

    /**
     * Get all nodes for a specific type, and optionally sorted by a field.
     *
     * @param  {string}                 type
     * @param  {QuerySort}              sort
     *
     * @return {Promise<ContentNode[]>}
     */
    public async getNodeList (type: string, sort: QuerySort): Promise<ContentNode[]> {
        const query = this.content(type);

        if (sort) {
            // For some reason the sort only applies correct if applied twice
            query
                .sortBy(sort.field, sort.direction)
                .sortBy(sort.field, sort.direction);
        }

        const nodes = await query.fetch();

        return nodes
            .map((node: unknown) => node as ContentNode)
            .filter((node: ContentNode) => {
                // Skip production filtering if running development environment
                if (this.isDev()) {
                    return true;
                }

                node.nodeType = this.getType(node);

                return node?.published ?? true;
            });
    }

    /**
     * Fetch from node list next and previous links for a given slug.
     *
     * @see: https://content.nuxt.com/v1/getting-started/fetching#surroundslugorpath-options
     *
     * @param {string} type
     * @param {string} slug
     * @param {string} sort
     *
     * @return NodeSiblingLinks
     */
    public async getNodeSiblings (node: ContentNode, sortBy: QuerySort): Promise<NodeSiblingLinks> {
        const [prev, next] = await this.content(node.nodeType)
            .only(["title", "path", "slug"])
            .sortBy(sortBy.field, sortBy.direction)
            .where({ slug: { $ne: "index" } })
            .surround(node.slug)
            .fetch() as FetchReturn[];

        return {
            prev: (prev as unknown) as SiblingLink,
            next: (next as unknown) as SiblingLink
        };
    }

    /**
     * Find a specific page by path.
     *
     * @param {string} path
     *
     * @returns ContentNodeInterface
     * @throws Error
     */
    public async getPage (path: string): Promise<PageContentNode> {
        let page = null;
        const nodes = await this.content("pages", path).fetch();

        // Exact match
        if (nodes instanceof Object) {
            page = nodes;
        }

        // Directory match, lookup index
        if (nodes instanceof Array) {
            page = nodes.find(node => node.slug === "index");
        }

        // Page not found
        if (!page) {
            throw new Error("Page could not be found");
        }

        // Set type
        const contentNode = (page as unknown) as PageContentNode;
        contentNode.nodeType = this.getType(contentNode);

        return contentNode;
    }

    /**
     * Get partial content item.
     *
     * @param {string} path
     *
     * @returns ContentNodeInterface
     * @throws Error
     */
    public async getPartial (path: string): Promise<PartialContentNode> {
        let partial = null;
        const nodes = await this.content("partials", path).fetch();

        // Exact match
        if (nodes instanceof Object) {
            partial = nodes;
        }

        // Directory match, lookup index
        if (nodes instanceof Array) {
            partial = nodes.find(node => node.slug === "index");
        }

        // Page not found
        if (!partial) {
            throw new Error("Partial could not be found");
        }

        // Set type
        const contentNode = (partial as unknown) as PartialContentNode;
        contentNode.nodeType = this.getType(contentNode);

        return contentNode;
    }

    /**
     * The home page content item.
     *
     * @returns ContentNodeInterface
     */
    public async homePage (): Promise<ContentNode> {
        return await this.getPage("index");
    }

    /**
     * Home page featured testimonials.
     *
     * @returns TestimonialNodeInterface[]
     */
    public async homeTestimonials (): Promise<TestimonialContentNode[]> {
        const featuredTestimonials = this.appContext.store.state.featuredTestimonials;
        const testimonialContent = await this.content("testimonials").fetch();

        return featuredTestimonials
            // Loop through featured items and match content by name in order
            .map((item: FeaturedTestimonialsItem) => {
                const content = testimonialContent.find((content: FetchReturn) => {
                    return content.name === item.name;
                });

                content.nodeType = NodeType.testimonial;

                return (content as unknown) as TestimonialContentNode;
            })
            // Filter out empty unmatched items
            .filter((item: TestimonialContentNode) => item.name);
    }

    /**
     * Helper to access and query content.
     *
     * @param {string}      type
     * @param {Object|null} options
     *
     * @returns QueryBuilder
     */
    protected content (type: string, options?: any): QueryBuilder {
        return this.appContext.$content(type, options ?? {});
    }
}
